/**
 * Badge Component
 * AIDLC Administrative Backend
 *
 * Reusable status badge following LDAJA design system.
 * Used for agent status, project state, priority levels, etc.
 */

import React from 'react';

type BadgeVariant = 'default' | 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'purple';

interface BadgeProps {
  children: React.ReactNode;
  variant?: BadgeVariant;
  dot?: boolean;
  className?: string;
}

const variantClasses: Record<BadgeVariant, { bg: string; text: string; dot: string }> = {
  default: { bg: 'bg-gray-100', text: 'text-gray-700', dot: 'bg-gray-500' },
  success: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  warning: { bg: 'bg-yellow-50', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  danger: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  info: { bg: 'bg-blue-50', text: 'text-ldaja-blue', dot: 'bg-ldaja-blue' },
  neutral: { bg: 'bg-gray-50', text: 'text-gray-500', dot: 'bg-gray-400' },
  purple: { bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
};

export function Badge({
  children,
  variant = 'default',
  dot = false,
  className = '',
}: BadgeProps) {
  const colors = variantClasses[variant] || variantClasses.default;

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full
        text-xs font-medium
        ${colors.bg} ${colors.text}
        ${className}
      `.trim()}
    >
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />}
      {children}
    </span>
  );
}
